/**
 * MedicalAlertBanner — compact read-only medical summary above tabs.
 * Driven from medical_alert_summary on student detail API.
 * Clickable "Action plan on file" navigates to Medical & Safety tab.
 */
import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Keep in sync with backend/sessions/medical_placeholders.py
const PLACEHOLDER_CHIPS = new Set([
  'none', 'n/a', 'na', 'nil', '-', '--', 'no', 'no allergies',
  'no known allergies', 'none known', 'nkda', 'no allergy', 'not applicable',
]);

function isPlaceholderChip(val) {
  const s = String(val || '').trim().toLowerCase();
  if (!s) return true;
  if (PLACEHOLDER_CHIPS.has(s)) return true;
  return s.startsWith('no known');
}

const SEVERITY_COLORS = {
  life_threatening: '#d32f2f',
  severe: '#e65100',
  moderate: '#f9a825',
  mild: '#558b2f',
};

const SEVERITY_LABELS = {
  life_threatening: 'Life-threatening',
  severe: 'Severe',
  moderate: 'Moderate',
  mild: 'Mild',
};

export default function MedicalAlertBanner({ summary, onActionPlanClick }) {
  if (!summary || !summary.has_alerts) return null;

  const allergies = (summary.allergies || []).filter((a) => !isPlaceholderChip(a.allergen));
  const conditions = (summary.conditions || []).filter((c) => !isPlaceholderChip(c));
  const hasActionPlan = !!summary.has_action_plan;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        bgcolor: '#fff3e0',
        border: '1px solid #ffb74d',
        borderRadius: 1,
        px: 2,
        py: 1.5,
        mb: 2,
      }}
    >
      <WarningAmberIcon sx={{ color: '#e65100', mt: 0.25 }} />
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: '#e65100', mb: 0.5 }}
        >
          Medical Alerts
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {allergies.map((a, i) => (
            <Chip
              key={`allergy-${i}`}
              size="small"
              label={`${a.allergen}${a.severity ? ` (${SEVERITY_LABELS[a.severity] || a.severity})` : ''}`}
              sx={{
                bgcolor: SEVERITY_COLORS[a.severity] || '#757575',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          ))}
          {conditions.map((c, i) => (
            <Chip
              key={`cond-${i}`}
              size="small"
              label={c}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
            />
          ))}
          {hasActionPlan && (
            <Chip
              size="small"
              label="Action plan on file"
              clickable
              onClick={onActionPlanClick}
              sx={{
                bgcolor: '#1565c0',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.75rem',
                '&:hover': { bgcolor: '#0d47a1' },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
